import React, { useState } from "react";
import { Archive, ArchiveRestore, MessageSquareOff, MoreVertical, PhoneOff, StickyNote, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Call from "@/model/interface/Call";
import type { DncType } from "@/model/interface/DncEntry";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MembershipRole } from "@/enum/MembershipRole";
import { MembershipStatus } from "@/enum/MembershipStatus";
import {
  useAddDncEntryMutation,
  useArchiveCallMutation,
  useDeleteCallPermanentlyMutation,
  useGetDncEntriesQuery,
  useGetHomeQuery,
  useRemoveFromDncListMutation,
  useRestoreCallMutation,
} from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";

interface CallActionsMenuProps {
  call: Call;
  onOpen?: () => void;
  onEditNotes?: () => void;
  onRemoved?: () => void;
}

const CallActionsMenu: React.FC<CallActionsMenuProps> = ({ call, onOpen, onEditNotes, onRemoved }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { data: dncEntries } = useGetDncEntriesQuery(
    { phoneNumbers: call.phoneNumber ? [call.phoneNumber] : [] },
    { skip: !menuOpen || !call.phoneNumber },
  );
  const { data: currentUser } = useGetHomeQuery();
  const [addDncEntry] = useAddDncEntryMutation();
  const [removeFromDncList] = useRemoveFromDncListMutation();
  const [archiveCall] = useArchiveCallMutation();
  const [restoreCall] = useRestoreCallMutation();
  const [deleteCallPermanently, { isLoading: isPurging }] = useDeleteCallPermanentlyMutation();
  const [confirmPurge, setConfirmPurge] = useState(false);
  const membership = currentUser?.activeMembership;
  const archived = !!call.deletedAt;
  const canDeletePermanently =
    membership?.status === MembershipStatus.ACTIVE &&
    (membership.role === MembershipRole.OWNER || membership.role === MembershipRole.ADMIN);
  const litigator = !!dncEntries?.items.some((entry) => entry.source === "LITIGATOR");

  const isOnDncList = (type: DncType) =>
    !!dncEntries?.items.some((entry) => entry.type === type || entry.type === "BOTH");

  const handleToggleDnc = async (type: DncType) => {
    if (!call.phoneNumber) {
      return;
    }
    const listName = type === "DNC" ? "Do Not Call" : "Do Not Text";
    try {
      if (isOnDncList(type)) {
        await removeFromDncList({ phoneNumber: call.phoneNumber, type }).unwrap();
        toast.success(`Removed from ${listName}`);
      } else {
        await addDncEntry({ phoneNumberE164: call.phoneNumber, type }).unwrap();
        toast.success(`Added to ${listName}`);
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, `Failed to update ${listName}`));
    }
  };

  const handleArchive = async () => {
    if (!call.id) {
      return;
    }
    try {
      await archiveCall(call.id).unwrap();
      toast.success("Call archived");
      onRemoved?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to archive call"));
    }
  };

  const handleRestore = async () => {
    if (!call.id) {
      return;
    }
    try {
      await restoreCall(call.id).unwrap();
      toast.success("Call restored");
      onRemoved?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to restore call"));
    }
  };

  const handlePurge = async () => {
    if (!call.id) {
      return;
    }
    try {
      await deleteCallPermanently(call.id).unwrap();
      toast.success("Call deleted permanently");
      setConfirmPurge(false);
      onRemoved?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to delete call"));
    }
  };

  return (
    <>
      <DropdownMenu
        onOpenChange={(open) => {
          setMenuOpen(open);
          if (open) {
            onOpen?.();
          }
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(event) => event.stopPropagation()} title="More actions">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onCloseAutoFocus={(event) => event.preventDefault()}>
          <DropdownMenuItem
            onClick={(event) => {
              event.stopPropagation();
              onEditNotes?.();
            }}
          >
            <StickyNote className="mr-2 h-4 w-4" />
            {call.notes ? "Edit notes" : "Add notes"}
          </DropdownMenuItem>
          {litigator ? (
            <DropdownMenuItem disabled>
              <PhoneOff className="mr-2 h-4 w-4" />
              Litigator, blocked
            </DropdownMenuItem>
          ) : (
            <>
              <DropdownMenuItem
                disabled={!dncEntries}
                onClick={(event) => {
                  event.stopPropagation();
                  void handleToggleDnc("DNC");
                }}
              >
                <PhoneOff className="mr-2 h-4 w-4" />
                {isOnDncList("DNC") ? "Remove from DNC" : "Add to DNC"}
              </DropdownMenuItem>
              {isOnDncList("DNC") ? (
                <DropdownMenuItem disabled>
                  <MessageSquareOff className="mr-2 h-4 w-4" />
                  Texts blocked by DNC
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  disabled={!dncEntries}
                  onClick={(event) => {
                    event.stopPropagation();
                    void handleToggleDnc("DNT");
                  }}
                >
                  <MessageSquareOff className="mr-2 h-4 w-4" />
                  {isOnDncList("DNT") ? "Remove from DNT" : "Add to DNT"}
                </DropdownMenuItem>
              )}
            </>
          )}
          <DropdownMenuSeparator />
          {archived ? (
            <>
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  void handleRestore();
                }}
              >
                <ArchiveRestore className="mr-2 h-4 w-4" />
                Restore
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!canDeletePermanently}
                className={canDeletePermanently ? "text-destructive focus:text-destructive" : undefined}
                onClick={(event) => {
                  event.stopPropagation();
                  setConfirmPurge(true);
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete permanently
              </DropdownMenuItem>
              {!canDeletePermanently && (
                <p className="px-2 py-1.5 text-xs text-muted-foreground">Only owners and admins can delete calls permanently.</p>
              )}
            </>
          ) : (
            <DropdownMenuItem
              onClick={(event) => {
                event.stopPropagation();
                void handleArchive();
              }}
            >
              <Archive className="mr-2 h-4 w-4" />
              Archive
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmPurge} onOpenChange={setConfirmPurge}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this call permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              The call and its recording are removed for everyone in the organisation. Billing records are kept. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handlePurge()}
              disabled={isPurging}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default CallActionsMenu;
